import { FunctionComponent, memo, useMemo } from "react";
import { Slider, Rail, Handles, Tracks, Ticks } from "react-compound-slider";
import { SliderRail, Handle, Track, Tick } from "components-for/slider";
import MetricSliderTickLabels from "./metric-slider-tick-labels";
import { IProps } from "./metric-slider.types";
import "./metric-slider.scss";

/**
 * Slider used to select the filter range
 * @param props.domain          Range domain for selection
 * @param props.highlightRange  Selected values on slider
 * @param props.metricData      Metric distribution data
 * @param props.metricType      Current metric selected
 * @param props.onChange        Callback to update values for inputs
 */
const MetricSlider: FunctionComponent<IProps> = ({
    disabled,
    domain,
    highlightRange,
    metricData,
    metricType,
    onChange,
}) => {
    const sliderStyle = {
        position: "relative",
        width: "100%",
    };

    /**
     * Create tick values from data buckets to match the chart bars
     */
    const tickValues = useMemo(() => {
        return [...metricData.map(({ minimumValue }) => minimumValue), metricData[metricData.length - 1].maximumValue];
    }, [metricData]);

    return (
        <div className="metric-slider">
            <Slider
                mode={3}
                step={1}
                domain={domain}
                rootStyle={sliderStyle}
                onChange={(data: any) => onChange(data.slice())} // Triggered when dragging has stoped
                values={highlightRange}
                disabled={disabled}
            >
                <Rail>
                    {({ getRailProps }) => (
                        <div className="metric-slider__rail">
                            <SliderRail getRailProps={getRailProps} />
                        </div>
                    )}
                </Rail>
                <Handles>
                    {({ handles, getHandleProps }) => (
                        <div className="metric-slider__handles">
                            {handles.map((handle) => (
                                <Handle key={handle.id} handle={handle} domain={domain} getHandleProps={getHandleProps}>
                                    {(tickValue) => (
                                        <MetricSliderTickLabels
                                            metricData={metricData}
                                            interactionMetric={metricType}
                                            tickValue={tickValue}
                                            tickIndex={0}
                                        />
                                    )}
                                </Handle>
                            ))}
                        </div>
                    )}
                </Handles>
                <Tracks left={false} right={false}>
                    {({ tracks, getTrackProps }) => (
                        <div className="metric-slider__tracks">
                            {tracks.map(({ id, source, target }) => (
                                <Track key={id} source={source} target={target} getTrackProps={getTrackProps} />
                            ))}
                        </div>
                    )}
                </Tracks>
                <Ticks count={metricData.length} values={tickValues}>
                    {({ ticks }) => {
                        return (
                            <div className="metric-slider__ticks">
                                {ticks.map((tick, index) => (
                                    <Tick key={tick.id} tick={tick} count={ticks.length}>
                                        {(tickValue) => (
                                            <MetricSliderTickLabels
                                                metricData={metricData}
                                                interactionMetric={metricType}
                                                tickValue={tickValue}
                                                tickIndex={index}
                                                isStyled={false}
                                            />
                                        )}
                                    </Tick>
                                ))}
                            </div>
                        );
                    }}
                </Ticks>
            </Slider>
        </div>
    );
};

export default memo(MetricSlider);
